import { useState } from 'react'
import PropTypes from 'prop-types'
import { apiFetch } from '../utils/api'

const Login = ({ onSwitchToRegister, onAdminLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await apiFetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        setSuccess('Login successful!')
        // The session lives in HttpOnly cookies; nothing is stored client-side.
        // Check if user is admin and redirect accordingly
        if (data.user?.role?.name === 'admin') {
          onAdminLogin(data.user)
        }
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (err) {
      setError('Network error. Please check if the backend server is running.')
      console.error('Login error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="login-container">
      {/* Logo */}
      <div className="logo">
        <h1>CodeNexus</h1>
        <p>Manage your licenses with ease</p>
      </div>

      {/* Login Title */}
      <div className="login-title">
        <h2>Login</h2>
        <p>Welcome back! Please sign in to your account.</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {/* Login Form */}
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
            required
            disabled={isLoading}
          />
        </div>

        <button 
          type="submit" 
          className="login-button"
          disabled={isLoading}
        >
          {isLoading && <span className="loading-spinner"></span>}
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      {/* Additional Links */}
      <div style={{ 
        marginTop: '24px', 
        textAlign: 'center', 
        fontSize: '0.875rem',
        color: '#9ca3af'
      }}>
        <p>
          Don't have an account?{' '}
          <button 
            type="button"
            onClick={onSwitchToRegister}
            style={{ 
              color: '#4f46e5', 
              background: 'none',
              border: 'none',
              textDecoration: 'none',
              fontWeight: '500',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            onFocus={(e) => e.target.style.textDecoration = 'underline'}
            onBlur={(e) => e.target.style.textDecoration = 'none'}
          >
            Sign up here
          </button>
        </p>
      </div>
    </div>
  )
}

Login.propTypes = {
  onSwitchToRegister: PropTypes.func.isRequired,
  onAdminLogin: PropTypes.func.isRequired
}

export default Login 